import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, PenLine, ShieldCheck, LogOut, Copy, AlertTriangle, Loader2, ShoppingCart, ArrowLeftRight, Droplets, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useWallet } from "@/contexts/WalletContext";
import { signInWithWallet, useWalletSession } from "@/lib/walletAuth";
import { PortfolioCard } from "@/components/wallet/Portfolio";
import { AddressHistory } from "@/components/explorer/AddressHistory";
import { DailyReward } from "@/components/wallet/DailyReward";
import { TestTokenNote } from "@/components/common/TestTokenNote";
import { RewardLeaderboard } from "@/components/wallet/RewardLeaderboard";

const errorText = (e) => {
  if (e?.code === 4001 || e?.code === "ACTION_REJECTED" || e?.info?.error?.code === 4001) return "Signature cancelled in your wallet";
  return e?.response?.data?.detail || e?.message || "Sign-in failed";
};
const date = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

const STEPS = [
  { icon: Wallet, title: "Connect", text: "Open MetaMask or another browser wallet." },
  { icon: PenLine, title: "Sign", text: "Sign a short message. Free, no gas, no transaction." },
  { icon: ShieldCheck, title: "You're in", text: "Claim a daily FMI reward and see your balances and activity." },
];

const Steps = ({ done }) => (
  <div className="grid sm:grid-cols-3 gap-3">
    {STEPS.map((s, i) => (
      <div key={s.title} data-testid={`account-step-${i + 1}`}
        className={`p-4 rounded-lg border transition-colors ${i < done ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : "border-[#2A2411] bg-[#050505]"}`}>
        <s.icon size={18} className={i < done ? "text-[#D4AF37]" : "text-zinc-500"} />
        <p className="mt-2 text-sm font-semibold text-white">{i + 1}. {s.title}</p>
        <p className="text-xs text-zinc-500 mt-1">{s.text}</p>
      </div>
    ))}
  </div>
);

const SignInCard = ({ address, onSignedIn }) => {
  const { connect, isConnecting, getProvider } = useWallet();
  const [busy, setBusy] = useState(false);
  const signIn = async () => {
    setBusy(true);
    try {
      await signInWithWallet(getProvider() || window.ethereum, address);
      toast.success("Signed in with your wallet");
      onSignedIn();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-[#D4AF37]/30 bg-[#0A0A0A]">
      <CardContent className="p-6 sm:p-8 space-y-6">
        <Steps done={address ? 1 : 0} />
        {address ? (
          <div className="flex flex-wrap items-center gap-4">
            <Button data-testid="account-sign-in-btn" onClick={signIn} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-11 px-6">
              {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <PenLine size={16} className="mr-2" />} Sign in with wallet
            </Button>
            <span className="text-xs text-zinc-500 font-mono">{address}</span>
          </div>
        ) : (
          <Button data-testid="account-connect-btn" onClick={connect} disabled={isConnecting} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-11 px-6">
            {isConnecting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Wallet size={16} className="mr-2" />} Connect wallet
          </Button>
        )}
        <p className="text-[11px] text-zinc-600">FSC will never ask for your seed phrase or private key. Signing in only proves you own the address.</p>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, testId }) => (
  <div><p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p><p data-testid={testId} className="text-sm text-white mt-0.5">{value}</p></div>
);

const ProfileCard = ({ session, onSignOut }) => {
  const copy = () => { navigator.clipboard.writeText(session.address); toast.success("Address copied"); };
  return (
    <Card data-testid="account-profile" className="border-[#D4AF37]/30 bg-[#0A0A0A]">
      <CardContent className="p-6 flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-4 min-w-0">
          <div className="h-12 w-12 shrink-0 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8a6d1d] grid place-items-center text-black"><ShieldCheck size={22} /></div>
          <div className="min-w-0">
            <p className="text-xs text-green-400 font-semibold">Signed in</p>
            <button data-testid="account-address" onClick={copy} className="font-mono text-sm sm:text-base text-white truncate max-w-full flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              {session.address} <Copy size={13} className="shrink-0" />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-8">
          <Stat label="Member since" value={date(session.firstLoginAt)} testId="account-member-since" />
          <Stat label="Last sign-in" value={date(session.lastLoginAt)} testId="account-last-login" />
          <Stat label="Sign-ins" value={session.loginCount} testId="account-login-count" />
        </div>
        <Button data-testid="account-sign-out-btn" variant="outline" onClick={onSignOut} className="border-[#2A2411] text-zinc-300"><LogOut size={14} className="mr-1.5" /> Sign out</Button>
      </CardContent>
    </Card>
  );
};

const QUICK = [
  { to: "/buy", label: "Buy FMI", icon: ShoppingCart }, { to: "/swap", label: "Swap", icon: ArrowLeftRight },
  { to: "/faucet", label: "Faucet", icon: Droplets }, { to: "/my-tokens", label: "My Tokens", icon: Coins },
];

const QuickLinks = () => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    {QUICK.map((q) => (
      <Link key={q.to} to={q.to} data-testid={`account-quick-${q.to.slice(1)}`}
        className="flex items-center gap-2 p-4 rounded-lg border border-[#2A2411] bg-[#0A0A0A] text-sm text-zinc-300 hover:border-[#D4AF37]/50 hover:text-white transition-colors">
        <q.icon size={16} className="text-[#D4AF37]" /> {q.label}
      </Link>
    ))}
  </div>
);

export default function AccountPage() {
  const { address } = useWallet();
  const { session, loading, refresh, signOut } = useWalletSession();
  const mismatch = session && address && address.toLowerCase() !== session.address;

  return (
    <div data-testid="account-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Web3 account</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mt-2">Your FSC wallet</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-xl">Connect and sign in with your wallet to see your balances, transactions and tokens on FMI Smart Chain in one place.</p>
        <TestTokenNote testId="account-test-token-note" className="mt-3" />
      </div>
      {loading && <Loader2 className="animate-spin text-[#D4AF37]" />}
      {!loading && !session && <SignInCard address={address} onSignedIn={refresh} />}
      {session && (
        <>
          <ProfileCard session={session} onSignOut={async () => { await signOut(); toast.success("Signed out"); }} />
          {mismatch && (
            <div data-testid="account-mismatch" className="flex flex-wrap items-center gap-3 p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5 text-sm text-yellow-200">
              <AlertTriangle size={16} /> Your wallet is now on a different address. Sign out and sign in again to switch accounts.
            </div>
          )}
          <DailyReward />
          <QuickLinks />
          <PortfolioCard address={session.address} />
          <AddressHistory address={session.address} />
        </>
      )}
      {!loading && <RewardLeaderboard me={session?.address} />}
    </div>
  );
}
